import { Moon, Sun } from 'lucide-react';
import { Hint } from '@/components/Explain';
import { cn } from '@/lib/cn';
import type { TimeZoneMode } from '@/lib/format';
import { VIEWS, type ViewId } from './view';

/**
 * The global shell chrome: wordmark, the functional-area nav, and the settings
 * that apply everywhere (theme, timezone). Feature toolbars (e.g. the bet
 * lookup bar) live inside each view, below this.
 */
export function Navbar({
  view,
  onView,
  theme,
  onToggleTheme,
  tz,
  onToggleTz,
  base,
  onBase,
  baseCurrencies,
}: {
  view: ViewId;
  onView: (v: ViewId) => void;
  theme: 'dark' | 'light';
  onToggleTheme: () => void;
  tz: TimeZoneMode;
  onToggleTz: () => void;
  /** Global base currency for converted figures — sits with the other display settings. */
  base: string;
  onBase: (base: string) => void;
  baseCurrencies: string[];
}) {
  return (
    <header className="sticky top-0 z-40 border-b border-hairline bg-bg/95 backdrop-blur">
      <div className="flex items-center gap-4 px-4 py-2">
        <span className="spectral select-none font-condensed text-[15px] font-semibold uppercase tracking-[0.18em]">
          Betlens
        </span>

        <nav className="flex items-center gap-0.5">
          {VIEWS.map((v) => {
            const Icon = v.icon;
            const active = v.id === view;
            const button = (
              <button
                key={v.id}
                type="button"
                onClick={() => onView(v.id)}
                className={cn(
                  'flex items-center gap-1.5 border-b-2 px-2.5 py-1.5 font-condensed text-[11px] font-semibold uppercase tracking-wider transition-colors',
                  active
                    ? 'border-b-accent text-accent'
                    : 'border-b-transparent text-text-faint hover:text-text-dim',
                  // Placeholders are reachable (they land on a "coming soon" page)
                  // but visibly not-yet-built.
                  !v.ready && !active && 'opacity-55',
                )}
              >
                <Icon size={13} />
                {v.label}
                {!v.ready && (
                  <span className="rounded-sm border border-hairline px-1 text-[8px] tracking-normal text-text-faint">
                    soon
                  </span>
                )}
              </button>
            );
            return (
              <Hint key={v.id} side="bottom" content={<span className="text-[12px]">{v.blurb}</span>}>
                {button}
              </Hint>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-1">
          {/* Base currency for FX-converted figures. A report filtered to one
              currency is exact and ignores this. */}
          <select
            value={base}
            onChange={(e) => onBase(e.target.value)}
            title="Base currency for converted totals — ignored when a report is filtered to a single currency"
            className="h-7 border border-hairline bg-transparent px-1 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text focus:border-accent focus:outline-none"
          >
            {(baseCurrencies.length ? baseCurrencies : [base]).map((c) => (
              <option key={c} value={c} className="bg-surface text-text">
                {c}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={onToggleTz}
            title={`Timestamps in ${tz === 'utc' ? 'UTC' : 'local time'} — click to switch`}
            className="flex h-7 min-w-7 items-center justify-center border border-hairline px-1.5 font-condensed text-[10px] font-semibold uppercase tracking-wider text-text-dim hover:border-hairline-strong hover:text-text"
          >
            {tz}
          </button>
          <button
            type="button"
            onClick={onToggleTheme}
            title="Switch theme"
            className="flex h-7 min-w-7 items-center justify-center border border-hairline px-1.5 text-text-dim hover:border-hairline-strong hover:text-text"
          >
            {theme === 'dark' ? <Sun size={13} /> : <Moon size={13} />}
          </button>
        </div>
      </div>
    </header>
  );
}
